// @spec GHG-STR-102
/**
 * TanStack Query hooks for GHG activity groups.
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ActivityGroup,
  CreateActivityGroupInput,
  UpdateActivityGroupInput,
} from './activity-groups';
import {
  createActivityGroup,
  deleteActivityGroup,
  fetchActivityGroups,
  updateActivityGroup,
} from './activity-groups';

import { useOrganizationId } from '@/hooks/use-organization-id';

export const activityGroupKeys = {
  all: ['ghg-activity-groups'] as const,
  list: (orgId: string) => [...activityGroupKeys.all, orgId] as const,
};

export function useActivityGroups() {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useOrganizationId();

  const query = useQuery({
    queryKey: activityGroupKeys.list(organizationId ?? ''),
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization not found');
      return fetchActivityGroups(getToken, organizationId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 30 * 1000,
  });

  return {
    groups: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateActivityGroup() {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<ActivityGroup, Error, CreateActivityGroupInput>({
    mutationFn: async (data) => {
      if (!organizationId) throw new Error('Organization not found');
      return createActivityGroup(getToken, organizationId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityGroupKeys.all });
    },
  });
}

export function useUpdateActivityGroup() {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<ActivityGroup, Error, { groupId: string; data: UpdateActivityGroupInput }>({
    mutationFn: async ({ groupId, data }) => {
      if (!organizationId) throw new Error('Organization not found');
      return updateActivityGroup(getToken, organizationId, groupId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityGroupKeys.all });
    },
  });
}

export function useDeleteActivityGroup() {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, string>({
    mutationFn: async (groupId) => {
      if (!organizationId) throw new Error('Organization not found');
      await deleteActivityGroup(getToken, organizationId, groupId);
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityGroupKeys.all });
    },
  });
}
